import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api, { formatErr } from "@/lib/api";
import { Plus, Buildings, CheckCircle, WarningCircle, Lightning, Megaphone, X, LinkSimple, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Agency() {
  const { user, refreshUser } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [brand, setBrand] = useState({
    brand_name: user?.agency_brand?.brand_name || "",
    primary_color: user?.agency_brand?.primary_color || "#002FA7",
    logo_url: user?.agency_brand?.logo_url || "",
    custom_domain: user?.agency_brand?.custom_domain || "",
    slug: user?.agency_brand?.slug || "",
    tagline: user?.agency_brand?.tagline || "",
    show_business_names: user?.agency_brand?.show_business_names || false,
    calendly_url: user?.agency_brand?.calendly_url || "",
    intentbrain_webhook_url: user?.agency_brand?.intentbrain_webhook_url || "",
  });

  useEffect(() => {
    if (user?.role !== "agency") { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get("/agency/clients");
        setClients(data);
      } catch {}
      setLoading(false);
    })();
  }, [user]);

  // Keep local `brand` state in sync with auth user. Critical so that flows like
  // the IntentBrain wizard (which updates the user via refreshUser) immediately
  // reflect the new webhook URL in the readiness card without a page reload.
  useEffect(() => {
    const ab = user?.agency_brand || {};
    setBrand((prev) => ({
      ...prev,
      brand_name: ab.brand_name ?? prev.brand_name,
      primary_color: ab.primary_color ?? prev.primary_color,
      logo_url: ab.logo_url ?? prev.logo_url,
      custom_domain: ab.custom_domain ?? prev.custom_domain,
      slug: ab.slug ?? prev.slug,
      tagline: ab.tagline ?? prev.tagline,
      show_business_names: ab.show_business_names ?? prev.show_business_names,
      calendly_url: ab.calendly_url ?? prev.calendly_url,
      intentbrain_webhook_url: ab.intentbrain_webhook_url ?? prev.intentbrain_webhook_url,
    }));
  }, [user?.agency_brand]);

  if (user?.role !== "agency") {
    return (
      <div className="p-12 max-w-3xl">
        <div className="bento p-12 text-center">
          <Buildings size={32} className="mx-auto text-zinc-400 mb-3" />
          <h2 className="font-display text-2xl font-bold mb-2">Agency mode is locked</h2>
          <p className="text-zinc-600">You're signed in as a business. Sign up as an agency to access whitelabel features.</p>
        </div>
      </div>
    );
  }

  const addClient = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/agency/clients", form);
      setClients([...clients, data]);
      setForm({ name: "", email: "", company: "" });
      setShowAdd(false);
      toast.success("Client added");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const saveBrand = async () => {
    try {
      await api.put("/agency/branding", brand);
      await refreshUser();
      toast.success("Branding saved");
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="p-6 lg:p-10 space-y-8 max-w-6xl">
      <header>
        <div className="label-tiny mb-1">Agency Hub</div>
        <h1 className="font-display text-4xl font-black tracking-tighter" data-testid="agency-title">Whitelabel Control Room</h1>
      </header>

      <section className="bento p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold">Clients</h2>
          <button className="btn-dark !py-2 !text-[11px]" onClick={() => setShowAdd(!showAdd)} data-testid="agency-add-client-toggle"><Plus size={12} weight="bold" /> Add client</button>
        </div>
        {showAdd && (
          <form onSubmit={addClient} className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="agency-add-form">
            <input className="input-field" placeholder="Name" required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} />
            <input className="input-field" type="email" placeholder="Email" required value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} />
            <input className="input-field" placeholder="Company" value={form.company} onChange={(e)=>setForm({...form, company: e.target.value})} />
            <button className="btn-primary justify-center" type="submit" data-testid="agency-add-submit">Add</button>
          </form>
        )}
        {loading ? (
          <div className="py-8 flex justify-center"><span className="spinner text-zinc-400"></span></div>
        ) : clients.length === 0 ? (
          <p className="text-zinc-500 text-sm">No clients yet. Add your first to start tracking.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr><th className="label-tiny pb-3 text-left">Name</th><th className="label-tiny pb-3 text-left">Email</th><th className="label-tiny pb-3 text-left">Company</th></tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} className="border-t border-zinc-100" data-testid={`client-${c.id}`}>
                  <td className="py-3 font-medium">{c.name}</td>
                  <td className="py-3 font-mono text-xs">{c.email}</td>
                  <td className="py-3">{c.company || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="bento p-6 space-y-4">
        <h2 className="font-display text-xl font-bold">Whitelabel Branding</h2>
        <p className="text-zinc-600 text-sm">Customize how your clients see the platform.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Brand name"><input className="input-field" value={brand.brand_name} onChange={(e)=>setBrand({...brand, brand_name: e.target.value})} data-testid="brand-name" /></Field>
          <Field label="Primary color"><input className="input-field" type="color" value={brand.primary_color} onChange={(e)=>setBrand({...brand, primary_color: e.target.value})} data-testid="brand-color" /></Field>
          <Field label="Logo URL"><input className="input-field" placeholder="https://..." value={brand.logo_url} onChange={(e)=>setBrand({...brand, logo_url: e.target.value})} data-testid="brand-logo" /></Field>
          <Field label="Custom domain"><input className="input-field" placeholder="app.youragency.com" value={brand.custom_domain} onChange={(e)=>setBrand({...brand, custom_domain: e.target.value})} data-testid="brand-domain" /></Field>
        </div>
        <button className="btn-primary" onClick={saveBrand} data-testid="brand-save">Save branding</button>
      </section>

      <section className="bento p-6 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-display text-xl font-bold">Public Showcase</h2>
            <p className="text-zinc-600 text-sm mt-1">A shareable, branded leaderboard of your client wins. Drop the link in cold outreach, LinkedIn, your website footer.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Slug"><input className="input-field" placeholder="your-agency-name" value={brand.slug} onChange={(e)=>setBrand({...brand, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,"-")})} data-testid="brand-slug" /></Field>
          <Field label="Tagline"><input className="input-field" placeholder="Putting local businesses on top of the map." value={brand.tagline} onChange={(e)=>setBrand({...brand, tagline: e.target.value})} data-testid="brand-tagline" /></Field>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Calendly URL (Demo CTA destination)"><input className="input-field" placeholder="https://calendly.com/your-agency/strategy" value={brand.calendly_url} onChange={(e)=>setBrand({...brand, calendly_url: e.target.value})} data-testid="brand-calendly" /></Field>
          <Field label="IntentBrain Webhook URL (Push prospects to)"><input className="input-field" placeholder="https://your-instance.intentbrain.ai/webhook/leads" value={brand.intentbrain_webhook_url} onChange={(e)=>setBrand({...brand, intentbrain_webhook_url: e.target.value})} data-testid="brand-intentbrain" /></Field>
        </div>
        <label className="flex items-center gap-3 text-sm cursor-pointer">
          <input type="checkbox" checked={!!brand.show_business_names} onChange={(e)=>setBrand({...brand, show_business_names: e.target.checked})} data-testid="brand-show-names" />
          <span>Show client business names publicly (otherwise anonymized to "[Category] client")</span>
        </label>
        {brand.slug && (
          <div className="p-3 bg-zinc-50 border-l-2 border-[#002FA7] text-sm font-mono break-all">
            Live URL: <a className="text-[#002FA7] font-semibold" href={`/agency/${brand.slug}`} target="_blank" rel="noopener noreferrer" data-testid="showcase-link">{window.location.origin}/agency/{brand.slug}</a>
          </div>
        )}
      </section>

      <GrowthStackBlueprint brand={brand} onConnected={refreshUser} />
    </div>
  );
}

function GrowthStackBlueprint({ brand, onConnected }) {
  const [showWizard, setShowWizard] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!brand.intentbrain_webhook_url) { setStats(null); return; }
    (async () => {
      try {
        const { data } = await api.get("/agency/intentbrain/status");
        setStats(data);
      } catch { /* non-fatal */ }
    })();
  }, [brand.intentbrain_webhook_url]);

  const checks = [
    {
      key: "authority",
      label: "Public Authority Showcase",
      sub: "Branded leaderboard URL prospects can visit",
      ok: !!brand.slug,
      fix: "Set a unique slug in the Public Showcase section above",
    },
    {
      key: "calendly",
      label: "Calendly Demo Routing",
      sub: "Where the Theatrical Demo CTA sends warm prospects",
      ok: !!brand.calendly_url,
      fix: "Add your Calendly URL in the Public Showcase section",
    },
    {
      key: "intentbrain",
      label: "IntentBrain Prospect Push Webhook",
      sub: "Live hand-off of Rank 4–30 GBPs into your AI Growth Engine",
      ok: !!brand.intentbrain_webhook_url,
      fix: "Paste your IntentBrain webhook URL above to activate prospect push",
      action: { label: brand.intentbrain_webhook_url ? "Reconnect" : "Connect IntentBrain", onClick: () => setShowWizard(true) },
    },
  ];
  const ready = checks.every((c) => c.ok);

  return (
    <>
    <section className="bento p-6 space-y-5" data-testid="growth-stack-blueprint">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-tiny mb-1">Agency Growth Stack</div>
          <h2 className="font-display text-xl font-bold">RankTrust.io + IntentBrain.ai blueprint</h2>
          <p className="text-zinc-600 text-sm mt-1 max-w-2xl">
            Two engines, one whitelabel stack. <span className="font-semibold">RankTrust</span> proves authority and retains clients.
            <span className="font-semibold"> IntentBrain</span> finds, qualifies, and routes prospects on autopilot.
          </p>
        </div>
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold ${ready ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
          {ready ? <CheckCircle size={14} weight="fill" /> : <WarningCircle size={14} weight="fill" />}
          {ready ? "Stack ready" : `${checks.filter((c) => c.ok).length}/${checks.length} configured`}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl p-5 border border-zinc-200 bg-white">
          <div className="flex items-center gap-2 mb-2">
            <Megaphone size={18} weight="bold" className="text-[#002FA7]" />
            <div className="font-semibold">Authority Engine — RankTrust.io</div>
          </div>
          <ul className="text-sm text-zinc-600 space-y-1.5">
            <li>• AI visibility audits & branded PDF reports</li>
            <li>• Public authority showcase to close prospects</li>
            <li>• Review/Q&A automation & rank tracking for retention</li>
          </ul>
        </div>
        <div className="rounded-xl p-5 border border-zinc-200 bg-white">
          <div className="flex items-center gap-2 mb-2">
            <Lightning size={18} weight="bold" className="text-emerald-600" />
            <div className="font-semibold">AI Growth Engine — IntentBrain.ai</div>
          </div>
          <ul className="text-sm text-zinc-600 space-y-1.5">
            <li>• Rank 4–30 prospect surfacing via Prospecting page</li>
            <li>• AI SDR qualifies & books via SMS / email / voice</li>
            <li>• Pipeline routing into your CRM on every hot lead</li>
          </ul>
        </div>
      </div>

      <div>
        <div className="label-tiny mb-2">Readiness checks</div>
        <div className="space-y-2">
          {checks.map((c) => (
            <div key={c.key} className="flex items-start gap-3 p-3 rounded-lg border border-zinc-200" data-testid={`stack-check-${c.key}`}>
              {c.ok ? (
                <CheckCircle size={18} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
              ) : (
                <WarningCircle size={18} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">{c.label}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{c.sub}</div>
                {!c.ok && <div className="text-xs text-amber-700 mt-1">→ {c.fix}</div>}
                {c.key === "intentbrain" && c.ok && stats && (
                  <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                    <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> IntentBrain connected
                    </span>
                    <span className="text-zinc-600">
                      Last push: <span className="font-semibold text-zinc-900">{formatRelative(stats.last_successful_push_at) || "no prospects routed yet"}</span>
                    </span>
                    <span className="text-zinc-600">
                      <span className="font-semibold text-zinc-900 tabular-nums">{stats.total_prospects_routed}</span> prospects routed
                    </span>
                    {stats.prospects_routed_last_30_days > 0 && (
                      <span className="text-zinc-500">
                        <span className="font-semibold text-zinc-700 tabular-nums">{stats.prospects_routed_last_30_days}</span> in last 30 days
                      </span>
                    )}
                    {stats.webhook_verification_stale ? (
                      <button
                        onClick={c.action?.onClick}
                        className="inline-flex items-center gap-1 font-semibold text-amber-700 hover:text-amber-900 underline-offset-2 hover:underline"
                        data-testid="intentbrain-retest-prompt"
                      >
                        {stats.last_webhook_verified_at
                          ? <>Last verified: <span className="font-bold">{formatRelative(stats.last_webhook_verified_at)}</span> — re-test connection?</>
                          : <>Never verified — re-test connection?</>}
                      </button>
                    ) : (
                      stats.last_webhook_verified_at && (
                        <span className="text-zinc-500">
                          Last verified: <span className="font-semibold text-zinc-700">{formatRelative(stats.last_webhook_verified_at)}</span>
                        </span>
                      )
                    )}
                  </div>
                )}
              </div>
              {c.action && (
                <button
                  onClick={c.action.onClick}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all hover:-translate-y-0.5 ${c.ok ? "bg-zinc-100 text-zinc-700 hover:bg-zinc-200" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}
                  data-testid={`stack-connect-${c.key}`}
                >
                  <LinkSimple size={12} weight="bold" />
                  {c.action.label}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
    {showWizard && (
      <IntentBrainWizard
        initialUrl={brand.intentbrain_webhook_url || ""}
        onClose={() => setShowWizard(false)}
        onConnected={async () => {
          await onConnected?.();
          setShowWizard(false);
        }}
      />
    )}
    </>
  );
}

function IntentBrainWizard({ initialUrl, onClose, onConnected }) {
  const [step, setStep] = useState(initialUrl ? 2 : 1); // 1=explain, 2=paste, 3=verifying/success
  const [url, setUrl] = useState(initialUrl);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null); // { ok: bool, status_code, error }

  const runTest = async (save = false) => {
    setTesting(true);
    setResult(null);
    try {
      const { data } = await api.post("/agency/intentbrain/test-webhook", { webhook_url: url.trim(), save });
      setResult(data);
      if (data.success && save) {
        toast.success("IntentBrain connected");
        await onConnected?.();
      } else if (data.success) {
        setStep(3);
      } else {
        toast.error(data.error || "Webhook test failed");
      }
    } catch (e) {
      const msg = formatErr(e);
      setResult({ success: false, error: msg });
      toast.error(msg);
    }
    setTesting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="intentbrain-wizard">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden">
        <div className="p-6 border-b border-zinc-200 flex items-start justify-between">
          <div>
            <div className="label-tiny mb-1">Connect IntentBrain.ai</div>
            <h3 className="font-display text-xl font-bold">AI Growth Engine setup</h3>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900 transition" data-testid="wizard-close">
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Step indicator */}
          <div className="flex items-center gap-2 text-xs">
            {[1, 2, 3].map((s) => (
              <div key={s} className={`flex-1 h-1 rounded-full ${s <= step ? "bg-emerald-600" : "bg-zinc-200"}`}></div>
            ))}
          </div>

          {step === 1 && (
            <div className="space-y-4">
              <div>
                <div className="text-sm font-bold mb-2">What is this?</div>
                <p className="text-sm text-zinc-600 leading-relaxed">
                  IntentBrain.ai is the <span className="font-semibold">AI Growth Engine</span> behind RankTrust. When you mark a
                  Rank 4–30 prospect in the Prospecting tab, RankTrust hands it off to IntentBrain to qualify, book, and follow up
                  on autopilot.
                </p>
              </div>
              <div>
                <div className="text-sm font-bold mb-2">What you need</div>
                <ol className="text-sm text-zinc-600 space-y-1.5 list-decimal pl-5">
                  <li>An IntentBrain.ai workspace.</li>
                  <li>The inbound <span className="font-mono text-xs bg-zinc-100 px-1 py-0.5 rounded">webhook URL</span> from your IntentBrain workflow (Settings → Inbound Webhooks).</li>
                  <li>That's it — paste it next.</li>
                </ol>
              </div>
              <button onClick={() => setStep(2)} className="w-full btn-primary justify-center" data-testid="wizard-step1-next">
                I have my webhook URL <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <span className="label-tiny block mb-2">IntentBrain webhook URL</span>
                <input
                  className="input-field"
                  placeholder="https://your-instance.intentbrain.ai/webhook/leads"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  data-testid="wizard-webhook-input"
                />
                <p className="text-xs text-zinc-500 mt-2">We'll send a single test prospect to verify the connection. No real data is pushed.</p>
              </div>

              {result && !result.success && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
                  <div className="font-bold mb-1">Connection failed</div>
                  <div className="font-mono break-all">{result.error || "Unknown error"}</div>
                </div>
              )}

              <div className="flex gap-2">
                <button onClick={() => setStep(1)} className="px-4 py-2.5 text-sm font-semibold text-zinc-600 hover:text-zinc-900" data-testid="wizard-back">
                  Back
                </button>
                <button
                  onClick={() => runTest(false)}
                  disabled={testing || !url.trim()}
                  className="flex-1 btn-dark justify-center"
                  data-testid="wizard-test-btn"
                >
                  {testing ? (
                    <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                  ) : (
                    <>Send test payload <ArrowRight size={14} weight="bold" /></>
                  )}
                </button>
              </div>
            </div>
          )}

          {step === 3 && result?.success && (
            <div className="space-y-4 text-center py-2">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 mx-auto">
                <CheckCircle size={28} weight="fill" className="text-emerald-600" />
              </div>
              <div>
                <div className="font-bold text-lg">Test successful</div>
                <p className="text-sm text-zinc-600 mt-1">
                  IntentBrain returned <span className="font-mono">{result.status_code}</span> — your webhook is reachable.
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setStep(2)} className="px-4 py-2.5 text-sm font-semibold text-zinc-600 hover:text-zinc-900" data-testid="wizard-edit">
                  Edit URL
                </button>
                <button
                  onClick={() => runTest(true)}
                  disabled={testing}
                  className="flex-1 btn-primary justify-center"
                  data-testid="wizard-save-btn"
                >
                  {testing ? (
                    <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                  ) : (
                    <>Save & activate IntentBrain <ArrowRight size={14} weight="bold" /></>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-2">
      <span className="label-tiny block">{label}</span>
      {children}
    </label>
  );
}

function formatRelative(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!t) return null;
  const diff = Math.floor((Date.now() - t) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}
